import { Card, CardContent, CardHeader, CardTitle, cn } from "@devhop/ui";
import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";

export type SummaryCardItem = {
  label: string;
  value?: ReactNode;
  children?: ReactNode;
};

/** สีตาม PARTZYNC Design System: Tech Blue, Action Orange, สถานะ ฯลฯ */
export type SummaryCardColor =
  | "default"
  | "primary"
  | "secondary"
  | "orange"
  | "muted"
  | "success"
  | "destructive";

const COLOR_STYLES: Record<
  SummaryCardColor,
  {
    iconBg: string;
    iconColor: string;
    titleColor: string;
    accentBorder: string;
  }
> = {
  default: {
    iconBg: "bg-secondary/10",
    iconColor: "text-secondary",
    titleColor: "text-foreground",
    accentBorder: "border-l-secondary",
  },
  primary: {
    iconBg: "bg-secondary/10",
    iconColor: "text-secondary",
    titleColor: "text-secondary",
    accentBorder: "border-l-secondary",
  },
  secondary: {
    iconBg: "bg-secondary/20",
    iconColor: "text-secondary",
    titleColor: "text-secondary",
    accentBorder: "border-l-secondary",
  },
  orange: {
    iconBg: "bg-primary/10",
    iconColor: "text-primary",
    titleColor: "text-primary",
    accentBorder: "border-l-primary",
  },
  muted: {
    iconBg: "bg-muted/30",
    iconColor: "text-muted-foreground",
    titleColor: "text-muted-foreground",
    accentBorder: "border-l-muted-foreground/50",
  },
  success: {
    iconBg: "bg-success/10",
    iconColor: "text-success",
    titleColor: "text-success",
    accentBorder: "border-l-success",
  },
  destructive: {
    iconBg: "bg-destructive/10",
    iconColor: "text-destructive",
    titleColor: "text-destructive",
    accentBorder: "border-l-destructive",
  },
};

type SummaryCardProps = {
  /** หัวข้อการ์ด */
  title: string;
  /** คำอธิบายเสริม (แสดงใต้ value ใน KPI, ใต้ title ใน grid) */
  description?: string;
  /** ไอคอน: KPI = มุมขวาบน size-4, Grid = กล่องซ้าย */
  icon?: LucideIcon;
  /** โทนสี (ไอคอน + หัวข้อ) ตาม PARTZYNC */
  color?: SummaryCardColor;
  /** "kpi" = แบบการ์ด KPI (title บน, value ใหญ่, ตรงกับ ShopPerformance) | "grid" = หลายแถว label-value */
  variant?: "kpi" | "grid";
  /** รายการ: KPI ใช้ items[0] เป็น value หลัก, Grid ใช้ทั้งหมด */
  items: SummaryCardItem[];
  /** จำนวนคอลัมน์ของ grid (variant="grid" เท่านั้น) */
  gridCols?: 2 | 3 | 4;
  /** คอลัมน์ที่ value span (variant="grid" เท่านั้น) */
  valueSpan?: 1 | 2 | 3;
  /** แสดงเส้น accent ด้านซ้าย (variant="grid" เท่านั้น) */
  accent?: boolean;
  /** บรรทัดใต้ value (KPI): เช่น เทรนด์ หรือข้อความเสริม */
  subtitle?: ReactNode;
  /** คลาสสำหรับ subtitle (เช่น text-success, text-destructive) */
  subtitleClassName?: string;
  className?: string;
  contentClassName?: string;
  titleClassName?: string;
};

/**
 * การ์ดสรุปข้อมูล: แบบ KPI (เหมือน ShopPerformance) หรือแบบ grid หลายแถว
 * รองรับ prop color ตาม PARTZYNC Design System
 */
export function SummaryCard({
  title,
  description,
  icon: Icon,
  color = "default",
  variant = "kpi",
  items,
  gridCols = 3,
  valueSpan = 2,
  accent = true,
  subtitle,
  subtitleClassName,
  className,
  contentClassName,
  titleClassName,
}: SummaryCardProps) {
  const theme = COLOR_STYLES[color];
  const mainValue = items[0]
    ? (items[0].children ?? items[0].value ?? "-")
    : "-";

  if (variant === "kpi") {
    return (
      <Card
        className={cn(
          "gap-0 rounded-xl border border-border bg-card p-4 transition-shadow hover:shadow-sm",
          className,
        )}
      >
        <div className="flex items-start justify-between">
          <p
            className={cn(
              "font-medium text-muted-foreground text-xs uppercase tracking-wide",
              titleClassName,
            )}
          >
            {title}
          </p>
          {Icon ? (
            <Icon
              className={cn("size-4 shrink-0", theme.iconColor)}
              aria-hidden
            />
          ) : null}
        </div>
        <p
          className={cn(
            "mt-2 font-bold text-2xl tracking-tight",
            contentClassName,
          )}
        >
          {mainValue}
        </p>
        {description ? (
          <p className="mt-1 text-muted-foreground text-xs">{description}</p>
        ) : null}
        {subtitle != null && !description ? (
          <div
            className={cn(
              "mt-1 flex items-center gap-1 text-xs",
              subtitleClassName,
            )}
          >
            {subtitle}
          </div>
        ) : null}
      </Card>
    );
  }

  // variant="grid"
  const gridClass = {
    2: "grid-cols-2",
    3: "grid-cols-3",
    4: "grid-cols-4",
  }[gridCols];
  const spanClass = {
    1: "col-span-1",
    2: "col-span-2",
    3: "col-span-3",
  }[valueSpan];

  return (
    <Card
      className={cn(
        "overflow-hidden rounded-xl border-border bg-card p-0 transition-shadow hover:shadow-sm",
        accent && `border-l-4 ${theme.accentBorder}`,
        className,
      )}
    >
      <CardHeader className="pt-4 pb-2">
        <CardTitle
          className={cn(
            "flex items-center gap-3 font-bold font-heading text-lg tracking-tight",
            titleClassName ?? theme.titleColor,
          )}
        >
          {Icon ? (
            <span
              className={cn(
                "flex size-10 shrink-0 items-center justify-center rounded-xl",
                theme.iconBg,
              )}
              aria-hidden
            >
              <Icon className={cn("size-5", theme.iconColor)} />
            </span>
          ) : null}
          <span className="min-w-0 flex-1">{title}</span>
        </CardTitle>
        {description ? (
          <p className="mt-1 text-muted-foreground text-sm">{description}</p>
        ) : null}
      </CardHeader>
      <CardContent className={cn("space-y-2 pb-4 text-sm", contentClassName)}>
        <div className={cn("grid gap-x-2 gap-y-2", gridClass)}>
          {items.map((item, idx) => {
            const content = item.children ?? item.value ?? "-";
            const hasLabel = item.label.trim().length > 0;
            return (
              <div key={`${item.label}-${idx}`} className="contents">
                {hasLabel ? (
                  <div className="text-muted-foreground">{item.label}</div>
                ) : null}
                <div
                  className={cn(
                    hasLabel ? spanClass : "col-span-full",
                    "wrap-break-word",
                  )}
                >
                  {content}
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
